/* CurriculumIO — small admin panel for exporting/importing the full training
 * curriculum as a JSON snapshot. Super-admin only (training:write enforced
 * server-side). Lives at the bottom of the Training Dashboard.
 *
 * Flow:
 *   1. Export — click "Export all" or pick a track, browser downloads JSON.
 *   2. Import — pick a .json file → server returns a diff preview → confirm.
 *      The preview tells the admin EXACTLY which topics/quizzes will be
 *      created vs. overwritten, so nothing destructive happens by surprise.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Download, Upload, FileJson, AlertTriangle, CheckCircle2, Loader2,
  X, RefreshCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CurriculumIO = () => {
  const [tracks, setTracks] = useState([]);
  const [trackId, setTrackId] = useState('');
  const [exporting, setExporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [overwrite, setOverwrite] = useState(true);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/docs`, { withCredentials: true })
      .then((r) => setTracks(r.data?.certification_tracks || []))
      .catch(() => setTracks([]));
  }, []);

  const handleExport = async () => {
    setExporting(true); setError('');
    try {
      const qs = trackId ? `?track_id=${encodeURIComponent(trackId)}` : '';
      const r = await axios.get(`${BACKEND_URL}/api/admin/curriculum/export${qs}`,
        { withCredentials: true, responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nhf-curriculum-${trackId || 'all'}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || 'Export failed.');
    }
    setExporting(false);
  };

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setPreviewFile(file); setError(''); setResult(null);
    setPreviewLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${BACKEND_URL}/api/admin/curriculum/import/preview`, fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not parse file.');
      setPreviewFile(null);
    }
    setPreviewLoading(false);
  }, []);

  const handleCommit = async () => {
    if (!previewFile) return;
    setImporting(true); setError('');
    try {
      const text = await previewFile.text();
      const payload = JSON.parse(text);
      const r = await axios.post(`${BACKEND_URL}/api/admin/curriculum/import`,
        { payload, overwrite_existing: overwrite },
        { withCredentials: true });
      setResult(r.data);
      setPreview(null);
      setPreviewFile(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Import failed.');
    }
    setImporting(false);
  };

  const reset = () => {
    setPreview(null); setPreviewFile(null); setResult(null); setError('');
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3"
      data-testid="curriculum-io">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-sm text-[#0A0A0A] inline-flex items-center gap-2">
          <FileJson className="w-4 h-4 text-[#BA1826]" /> Curriculum Import / Export
        </h2>
        <p className="text-[10px] text-[#9CA3AF] italic">
          Snapshot of all training topics + quizzes as portable JSON.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded inline-flex items-start gap-2"
          data-testid="curriculum-error">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {error}
        </div>
      )}

      {/* ===== Export ===== */}
      <div className="border border-[#E5E7EB] rounded p-3 space-y-2">
        <p className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">Export</p>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF] mb-1">
              Scope
            </label>
            <select value={trackId} onChange={(e) => setTrackId(e.target.value)}
              className="px-2 py-1.5 border border-[#E5E7EB] rounded text-xs bg-white"
              data-testid="curriculum-export-scope">
              <option value="">All tracks (full curriculum)</option>
              {tracks.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <button onClick={handleExport} disabled={exporting}
            className="text-xs px-3 py-1.5 bg-[#0A0A0A] text-white rounded inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="curriculum-export-btn">
            {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            Download JSON
          </button>
        </div>
      </div>

      {/* ===== Import ===== */}
      <div className="border border-[#E5E7EB] rounded p-3 space-y-2">
        <p className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">Import</p>
        {!preview && !result && (
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-xs px-3 py-1.5 border border-[#BA1826]/40 bg-[#FFF8F8] text-[#BA1826] rounded cursor-pointer inline-flex items-center gap-1.5 hover:bg-[#BA1826] hover:text-white transition-colors"
              data-testid="curriculum-import-label">
              {previewLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              Choose JSON file…
              <input type="file" accept="application/json,.json"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="curriculum-import-file" />
            </label>
            <p className="text-[10px] text-[#9CA3AF]">
              Schema: <code className="font-mono">nhf.curriculum.v1</code>
            </p>
          </div>
        )}

        {preview && (
          <div className="space-y-2" data-testid="curriculum-preview">
            <p className="text-xs font-semibold text-[#0A0A0A]">
              Preview: <span className="font-mono">{previewFile?.name}</span>
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <PreviewStat label="New topics" n={preview.new_topics?.length || 0} accent="emerald" />
              <PreviewStat label="Updated topics" n={preview.updated_topics?.length || 0} accent={overwrite ? 'amber' : 'neutral'} />
              <PreviewStat label="New quizzes" n={preview.new_quizzes?.length || 0} accent="emerald" />
              <PreviewStat label="Updated quizzes" n={preview.updated_quizzes?.length || 0} accent={overwrite ? 'amber' : 'neutral'} />
            </div>
            <label className="flex items-center gap-1.5 text-xs text-[#4B5563]">
              <input type="checkbox" checked={overwrite}
                onChange={(e) => setOverwrite(e.target.checked)}
                data-testid="curriculum-overwrite" />
              Overwrite existing topics &amp; quizzes (uncheck to import only new items)
            </label>
            <div className="flex items-center gap-2 pt-1">
              <button onClick={handleCommit} disabled={importing}
                className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded inline-flex items-center gap-1.5 disabled:opacity-60"
                data-testid="curriculum-import-commit">
                {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                Confirm import
              </button>
              <button onClick={reset}
                className="text-xs px-2.5 py-1.5 border border-[#E5E7EB] rounded inline-flex items-center gap-1.5"
                data-testid="curriculum-import-cancel">
                <X className="w-3 h-3" /> Cancel
              </button>
            </div>
          </div>
        )}

        {result && (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-2.5 text-xs space-y-1"
            data-testid="curriculum-result">
            <p className="font-bold text-emerald-800 inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> Import complete
            </p>
            <ul className="text-emerald-700 ml-5 list-disc">
              <li>Topics added: {result.topics_added} · updated: {result.topics_updated} · skipped: {result.topics_skipped}</li>
              <li>Quizzes added: {result.quizzes_added} · updated: {result.quizzes_updated} · skipped: {result.quizzes_skipped}</li>
            </ul>
            <button onClick={reset}
              className="text-[11px] text-emerald-800 underline inline-flex items-center gap-1 mt-1"
              data-testid="curriculum-result-reset">
              <RefreshCw className="w-3 h-3" /> Import another file
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const PreviewStat = ({ label, n, accent = 'neutral' }) => {
  const colors = {
    neutral: 'bg-white border-[#E5E7EB] text-[#0A0A0A]',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
  };
  return (
    <div className={`border rounded p-2 ${colors[accent]}`}>
      <p className="text-[9px] uppercase tracking-wider font-bold opacity-80">{label}</p>
      <p className="text-lg font-bold font-mono">{n}</p>
    </div>
  );
};

export default CurriculumIO;
